/**
 * CMMC / FAR practice catalogs, company-project-contract taxonomy,
 * and the rule engine that unlocks SPRS, SSP/POA&M, L2 rows, and CUI enclave
 * work at the right level. Not a C3PAO and not a SPRS filing system.
 */

export type CmmcLevel = 'none' | 'l1' | 'l2' | 'l3';
export type DataHandling = 'none' | 'fci' | 'cui' | 'classified';
export type DodAssessmentPath = 'none' | 'self' | 'c3pao' | 'dibcac';
export type PracticeDisposition = 'not_started' | 'partial' | 'met' | 'not_met' | 'na' | 'poam';

export type DodCapabilityId =
  | 'l1_15_practices'
  | 'sprs_l1_affirmation'
  | 'sprs_numeric_scoring'
  | 'ssp_poam'
  | 'gcc_high_entra_intune_evidence'
  | 'l2_110_practices'
  | 'preveil_cui_mailbox'
  | 'facility_clearance';

export type DodPractice = {
  id: string;
  nistId?: string;
  farId?: string;
  family: string;
  title: string;
  level: 'l1' | 'l2';
  sprsWeight: 1 | 5;
  sourceUrl: string;
};

export type DodCompanyRecord = {
  id: string;
  name: string;
  ein?: string;
  uei?: string;
  cage?: string;
};

export type DodProjectRecord = {
  id: string;
  companyId: string;
  name: string;
  description: string;
  dataHandling: DataHandling;
};

export type DodContractRecord = {
  id: string;
  projectId: string;
  name: string;
  solicitation?: string;
  agency?: string;
  clauseText: string;
  requiredLevel: CmmcLevel;
  assessmentPath: DodAssessmentPath;
};

export type DodTaxonomyState = {
  companies: DodCompanyRecord[];
  projects: DodProjectRecord[];
  contracts: DodContractRecord[];
  activeCompanyId: string;
  activeProjectId: string;
  activeContractId: string;
};

export type DodEvidenceConnectors = {
  gccHigh: boolean;
  entra: boolean;
  intune: boolean;
  preveilMailbox: boolean;
};

export const emptyDodEvidenceConnectors = (): DodEvidenceConnectors => ({
  gccHigh: false,
  entra: false,
  intune: false,
  preveilMailbox: false,
});

export type DodLevelAssessment = {
  companyLevel: CmmcLevel;
  contractLevel: CmmcLevel;
  dataHandling: DataHandling;
  assessmentPath: DodAssessmentPath;
  unlocks: DodCapabilityId[];
  rationale: string;
  source: 'rules' | 'ai';
  classifiedAt: string;
};

export type DodClassifyInput = {
  companyName?: string;
  projectName?: string;
  projectDescription?: string;
  contractName?: string;
  clauseText?: string;
  dataHandling?: DataHandling;
  companyHasSam?: boolean;
  companyHasSprsL1?: boolean;
  companyHasNist800171?: boolean;
};

export const CMMC_LEVEL_LABELS: Record<CmmcLevel, { en: string; es: string }> = {
  none: { en: 'Not in CMMC scope', es: 'Fuera de CMMC' },
  l1: { en: 'CMMC Level 1 (FCI / Self)', es: 'CMMC Nivel 1 (FCI / Autoeval.)' },
  l2: { en: 'CMMC Level 2 (CUI)', es: 'CMMC Nivel 2 (CUI)' },
  l3: { en: 'CMMC Level 3 (priority / DIBCAC)', es: 'CMMC Nivel 3 (prioridad / DIBCAC)' },
};

export const DOD_CAPABILITY_CATALOG: Record<
  DodCapabilityId,
  { title: string; when: string; offerAt: CmmcLevel[]; partnerHint?: string }
> = {
  l1_15_practices: {
    title: 'Level 1 practices',
    when: '15 FAR 52.204-21 practices for every FCI contract. Mark MET or NOT MET only — Level 1 has no POA&M credit.',
    offerAt: ['l1', 'l2', 'l3'],
  },
  sprs_l1_affirmation: {
    title: 'SPRS Level 1 affirmation',
    when: 'Annual self-affirmation in SPRS after CAGE and PIEE access. A managing member affirms; this is not a numeric 800-171 score.',
    offerAt: ['l1', 'l2', 'l3'],
  },
  sprs_numeric_scoring: {
    title: 'SPRS numeric score',
    when: 'NIST 800-171 score from −203 to 110. Only when the contract requires CUI / Level 2. Do not generate this number for FCI-only work.',
    offerAt: ['l2', 'l3'],
  },
  ssp_poam: {
    title: 'SSP and POA&M',
    when: 'System Security Plan and Plan of Action needed before bidding CUI. Not required to claim Level 1.',
    offerAt: ['l2', 'l3'],
  },
  gcc_high_entra_intune_evidence: {
    title: 'GCC High / Entra / Intune',
    when: 'Point at evidence in a Microsoft government tenant when CUI lives there. Do not pull CUI into commercial NextCompli.',
    offerAt: ['l2', 'l3'],
  },
  l2_110_practices: {
    title: 'Level 2 practices',
    when: '110 NIST SP 800-171 Rev. 2 practices. Unlock when the contract handles CUI or names CMMC Level 2.',
    offerAt: ['l2', 'l3'],
  },
  preveil_cui_mailbox: {
    title: 'CUI mailbox',
    when: 'Encrypted mailbox or Drive enclave (PreVeil or GCC High) when CUI or ITAR technical data will be emailed or shared. PreVeil free accounts are collaboration-only; CMMC Pass is paid.',
    offerAt: ['l2', 'l3'],
    partnerHint: 'https://www.preveil.com/',
  },
  facility_clearance: {
    title: 'Clearance path',
    when: 'Facility or personnel clearance only if a classified solicitation requires it. Unclassified IT consulting stays N/A.',
    offerAt: ['l3'],
  },
};

const FAR_L1: Array<{ farId: string; family: string; title: string }> = [
  { farId: 'b.1.i', family: 'Access Control', title: 'Limit system access to authorized users, processes, and devices' },
  { farId: 'b.1.ii', family: 'Access Control', title: 'Limit access to the types of transactions and functions authorized users may execute' },
  { farId: 'b.1.iii', family: 'Access Control', title: 'Verify and control connections to external information systems' },
  { farId: 'b.1.iv', family: 'Access Control', title: 'Control information posted or processed on publicly accessible systems' },
  { farId: 'b.1.v', family: 'Identification and Authentication', title: 'Identify system users, processes acting on behalf of users, and devices' },
  { farId: 'b.1.vi', family: 'Identification and Authentication', title: 'Authenticate (or verify) the identities of those users, processes, or devices' },
  { farId: 'b.1.vii', family: 'Media Protection', title: 'Sanitize or destroy media containing FCI before disposal or reuse' },
  { farId: 'b.1.viii', family: 'Physical Protection', title: 'Limit physical access to systems, equipment, and operating environments' },
  { farId: 'b.1.ix', family: 'Physical Protection', title: 'Escort visitors, monitor visitor activity, log physical access, and control access devices' },
  { farId: 'b.1.x', family: 'System and Communications Protection', title: 'Monitor, control, and protect communications at external and key internal boundaries' },
  { farId: 'b.1.xi', family: 'System and Communications Protection', title: 'Separate publicly accessible system components from internal networks' },
  { farId: 'b.1.xii', family: 'System and Information Integrity', title: 'Identify, report, and correct information-system flaws in a timely manner' },
  { farId: 'b.1.xiii', family: 'System and Information Integrity', title: 'Provide malicious-code protection at appropriate locations' },
  { farId: 'b.1.xiv', family: 'System and Information Integrity', title: 'Update malicious-code protection mechanisms when new releases are available' },
  { farId: 'b.1.xv', family: 'System and Information Integrity', title: 'Scan systems periodically and scan files from external sources in real time' },
];

export const CMMC_L1_PRACTICES: DodPractice[] = FAR_L1.map((row) => ({
  id: `l1-${row.farId.replace(/\./g, '-')}`,
  farId: row.farId,
  family: row.family,
  title: row.title,
  level: 'l1',
  sprsWeight: 1,
  sourceUrl: 'https://www.acquisition.gov/far/52.204-21',
}));

/** 5-point NIST 800-171 Rev. 2 requirements used for SPRS numeric scoring (DoD Assessment Methodology). */
const SPRS_FIVE_POINT = new Set([
  '3.1.5', '3.1.12', '3.1.13', '3.1.16', '3.1.17', '3.1.18',
  '3.4.5', '3.4.6', '3.4.7', '3.4.8',
  '3.5.3', '3.5.10',
  '3.7.5',
  '3.8.4', '3.8.5',
  '3.13.6', '3.13.8', '3.13.11', '3.13.16',
  '3.14.1',
]);

const L2_FAMILY_TITLES: Array<{ family: string; prefix: string; titles: string[] }> = [
  {
    family: 'Access Control',
    prefix: '3.1',
    titles: [
      'Limit system access to authorized users',
      'Limit access to authorized transactions and functions',
      'Control CUI flow',
      'Separate duties of individuals',
      'Employ least privilege',
      'Use non-privileged accounts for non-security functions',
      'Prevent non-privileged users from executing privileged functions',
      'Limit unsuccessful logon attempts',
      'Provide privacy and security notices',
      'Use session lock',
      'Terminate sessions after inactivity',
      'Monitor and control remote access',
      'Employ cryptographic mechanisms for remote access',
      'Route remote access through managed access control points',
      'Authorize remote execution of privileged commands',
      'Authorize wireless access',
      'Protect wireless access with authentication and encryption',
      'Control mobile devices',
      'Encrypt CUI on mobile devices and platforms',
      'Control use of external systems',
      'Limit use of portable storage on external systems',
      'Control CUI posted on publicly accessible systems',
    ],
  },
  {
    family: 'Awareness and Training',
    prefix: '3.2',
    titles: [
      'Ensure managers, system administrators, and users are aware of security risks',
      'Ensure personnel are trained to carry out assigned information-security duties',
      'Provide security awareness training on recognizing and reporting insider threats',
    ],
  },
  {
    family: 'Audit and Accountability',
    prefix: '3.3',
    titles: [
      'Create and retain system audit logs',
      'Ensure the actions of individual users can be uniquely traced',
      'Review and update logged events',
      'Alert in the event of an audit logging process failure',
      'Correlate audit record review, analysis, and reporting',
      'Provide audit record reduction and report generation',
      'Provide a system capability that compares and synchronizes internal clock times',
      'Protect audit information and audit logging tools from unauthorized access',
      'Limit management of audit logging functionality to a subset of privileged users',
    ],
  },
  {
    family: 'Configuration Management',
    prefix: '3.4',
    titles: [
      'Establish and maintain baseline configurations and inventories',
      'Establish and enforce security configuration settings',
      'Track, review, approve or disapprove, and log changes',
      'Analyze security impact of changes prior to implementation',
      'Define, document, approve, and enforce physical and logical access restrictions',
      'Employ the principle of least functionality',
      'Restrict, disable, or prevent the use of nonessential programs, functions, ports, protocols, and services',
      'Apply deny-by-exception policy to prevent the use of unauthorized software',
      'Control and monitor user-installed software',
    ],
  },
  {
    family: 'Identification and Authentication',
    prefix: '3.5',
    titles: [
      'Identify system users, processes, and devices',
      'Authenticate the identities of those users, processes, or devices',
      'Use multifactor authentication for local and network access to privileged accounts and for network access to non-privileged accounts',
      'Employ replay-resistant authentication mechanisms',
      'Prevent reuse of identifiers for a defined period',
      'Disable identifiers after a defined period of inactivity',
      'Enforce a minimum password complexity and change of characters',
      'Prohibit password reuse for a specified number of generations',
      'Allow temporary password use for system logons with an immediate change to a permanent password',
      'Store and transmit only cryptographically-protected passwords',
      'Obscure feedback of authentication information',
    ],
  },
  {
    family: 'Incident Response',
    prefix: '3.6',
    titles: [
      'Establish an operational incident-handling capability',
      'Track, document, and report incidents to designated officials and authorities',
      'Test the organizational incident response capability',
    ],
  },
  {
    family: 'Maintenance',
    prefix: '3.7',
    titles: [
      'Perform maintenance on systems',
      'Provide controls on the tools, techniques, mechanisms, and personnel used to conduct maintenance',
      'Ensure equipment removed from the premises is sanitized of any CUI',
      'Check media containing diagnostic and test programs for malicious code before use',
      'Require multifactor authentication to establish nonlocal maintenance sessions',
      'Supervise the maintenance activities of maintenance personnel without required access authorization',
    ],
  },
  {
    family: 'Media Protection',
    prefix: '3.8',
    titles: [
      'Protect paper and digital CUI media',
      'Limit access to CUI on system media to authorized users',
      'Sanitize or destroy system media containing CUI before disposal or reuse',
      'Mark media with applicable CUI markings',
      'Control access to media containing CUI and maintain accountability',
      'Implement cryptographic mechanisms to protect the confidentiality of CUI stored on digital media during transport',
      'Control the use of removable media on system components',
      'Prohibit the use of portable storage devices when such devices have no identifiable owner',
      'Protect the confidentiality of backup CUI at storage locations',
    ],
  },
  {
    family: 'Personnel Security',
    prefix: '3.9',
    titles: [
      'Screen individuals prior to authorizing access to systems containing CUI',
      'Ensure CUI and systems containing CUI are protected during and after personnel actions such as terminations and transfers',
    ],
  },
  {
    family: 'Physical Protection',
    prefix: '3.10',
    titles: [
      'Limit physical access to systems, equipment, and operating environments',
      'Protect and monitor the physical facility and support infrastructure',
      'Escort visitors and monitor visitor activity',
      'Maintain audit logs of physical access',
      'Control and manage physical access devices',
      'Enforce safeguarding measures for CUI at alternate work sites',
    ],
  },
  {
    family: 'Risk Assessment',
    prefix: '3.11',
    titles: [
      'Periodically assess the risk to organizational operations, assets, and individuals',
      'Scan for vulnerabilities in systems and applications periodically and when new vulnerabilities affecting those systems and applications are identified',
      'Remediate vulnerabilities in accordance with risk assessments',
    ],
  },
  {
    family: 'Security Assessment',
    prefix: '3.12',
    titles: [
      'Periodically assess the security controls in organizational systems',
      'Develop and implement plans of action designed to correct deficiencies',
      'Monitor security controls on an ongoing basis',
      'Develop, document, and periodically update system security plans',
    ],
  },
  {
    family: 'System and Communications Protection',
    prefix: '3.13',
    titles: [
      'Monitor, control, and protect communications at external and key internal boundaries',
      'Employ architectural designs, software development techniques, and systems engineering principles that promote effective information security',
      'Separate user functionality from system management functionality',
      'Prevent unauthorized and unintended information transfer via shared system resources',
      'Implement subnetworks for publicly accessible system components that are physically or logically separated from internal networks',
      'Deny network communications traffic by default and allow by exception',
      'Prevent remote devices from simultaneously establishing non-remote connections with organizational systems and communicating via some other connection to resources in external networks (split tunneling)',
      'Implement cryptographic mechanisms to prevent unauthorized disclosure of CUI during transmission',
      'Terminate network connections associated with communications sessions at the end of the sessions or after a defined period of inactivity',
      'Establish and manage cryptographic keys',
      'Employ FIPS-validated cryptography when used to protect the confidentiality of CUI',
      'Prohibit remote activation of collaborative computing devices',
      'Control and monitor the use of mobile code',
      'Control and monitor the use of Voice over Internet Protocol (VoIP) technologies',
      'Protect the authenticity of communications sessions',
      'Protect the confidentiality of CUI at rest',
    ],
  },
  {
    family: 'System and Information Integrity',
    prefix: '3.14',
    titles: [
      'Identify, report, and correct system flaws in a timely manner',
      'Provide protection from malicious code at designated locations',
      'Monitor system security alerts and advisories and take action in response',
      'Update malicious code protection mechanisms when new releases are available',
      'Perform periodic scans of systems and real-time scans of files from external sources',
      'Monitor organizational systems including inbound and outbound communications traffic',
      'Identify unauthorized use of organizational systems',
    ],
  },
];

export const CMMC_L2_PRACTICES: DodPractice[] = L2_FAMILY_TITLES.flatMap((family) => (
  family.titles.map((title, index) => {
    const nistId = `${family.prefix}.${index + 1}`;
    return {
      id: `l2-${nistId.replace(/\./g, '-')}`,
      nistId,
      family: family.family,
      title,
      level: 'l2' as const,
      sprsWeight: (SPRS_FIVE_POINT.has(nistId) ? 5 : 1) as 1 | 5,
      sourceUrl: 'https://csrc.nist.gov/pubs/sp/800/171/r2/final',
    };
  })
));

export const practicesForLevel = (level: CmmcLevel): DodPractice[] => {
  if (level === 'l3' || level === 'l2') {
    return [...CMMC_L1_PRACTICES, ...CMMC_L2_PRACTICES];
  }
  if (level === 'l1') {
    return CMMC_L1_PRACTICES;
  }
  return [];
};

export const emptyDodTaxonomy = (): DodTaxonomyState => ({
  companies: [],
  projects: [],
  contracts: [],
  activeCompanyId: '',
  activeProjectId: '',
  activeContractId: '',
});

export const newDodId = (prefix: string): string => (
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
);

const LEVEL_RANK: Record<CmmcLevel, number> = { none: 0, l1: 1, l2: 2, l3: 3 };

export const maxCmmcLevel = (left: CmmcLevel, right: CmmcLevel): CmmcLevel => (
  LEVEL_RANK[left] >= LEVEL_RANK[right] ? left : right
);

export const unlocksForLevel = (contractLevel: CmmcLevel, dataHandling: DataHandling): DodCapabilityId[] => {
  const ids = (Object.keys(DOD_CAPABILITY_CATALOG) as DodCapabilityId[]).filter((id) => (
    DOD_CAPABILITY_CATALOG[id].offerAt.includes(contractLevel)
  ));
  if (dataHandling !== 'cui' && dataHandling !== 'classified') {
    return ids.filter((id) => id !== 'preveil_cui_mailbox' && id !== 'facility_clearance');
  }
  if (dataHandling !== 'classified') {
    return ids.filter((id) => id !== 'facility_clearance');
  }
  return ids;
};

const CLASSIFIED_HINTS = [
  'classified', 'secret', 'top secret', 'ts/sci', 'facility clearance', 'fcl', 'nispom', 'dd254',
];
const CUI_HINTS = [
  'cui', 'controlled unclassified', '252.204-7012', 'dfars 7012', 'nist 800-171', 'nist sp 800-171',
  'cmmc level 2', 'cmmc l2', 'c3pao', 'covered defense information',
];
const L3_HINTS = ['cmmc level 3', 'cmmc l3', '800-172', 'dibcac'];
const FCI_HINTS = [
  'fci', 'federal contract information', '52.204-21', 'cmmc level 1', 'cmmc l1', 'basic safeguarding',
];

export const inferDataHandling = (text: string, explicit?: DataHandling): DataHandling => {
  if (explicit && explicit !== 'none') {
    return explicit;
  }
  const haystack = String(text || '').toLowerCase();
  const classifiedHaystack = haystack.replace(/unclassified/g, ' ');
  if (CLASSIFIED_HINTS.some((hint) => classifiedHaystack.includes(hint))) {
    return 'classified';
  }
  if (CUI_HINTS.some((hint) => haystack.includes(hint)) || L3_HINTS.some((hint) => haystack.includes(hint))) {
    return 'cui';
  }
  if (FCI_HINTS.some((hint) => haystack.includes(hint))) {
    return 'fci';
  }
  return 'none';
};

export const classifyDodLevel = (input: DodClassifyInput): DodLevelAssessment => {
  const blob = [
    input.companyName,
    input.projectName,
    input.projectDescription,
    input.contractName,
    input.clauseText,
  ].filter(Boolean).join('\n');
  const dataHandling = inferDataHandling(blob, input.dataHandling);
  let contractLevel: CmmcLevel = 'none';
  let assessmentPath: DodAssessmentPath = 'none';
  if (dataHandling === 'classified' || L3_HINTS.some((hint) => blob.toLowerCase().includes(hint))) {
    contractLevel = 'l3';
    assessmentPath = 'dibcac';
  } else if (dataHandling === 'cui') {
    contractLevel = 'l2';
    assessmentPath = blob.toLowerCase().includes('c3pao') ? 'c3pao' : 'self';
  } else if (dataHandling === 'fci') {
    contractLevel = 'l1';
    assessmentPath = 'self';
  }

  let companyLevel: CmmcLevel = 'none';
  if (input.companyHasNist800171) {
    companyLevel = 'l2';
  } else if (input.companyHasSprsL1) {
    companyLevel = 'l1';
  } else if (input.companyHasSam) {
    companyLevel = 'none';
  }

  const rationaleParts = [
    contractLevel === 'none'
      ? 'No FCI/CUI/classified signal yet. Stay on entity eligibility until a solicitation is classified.'
      : `Contract requirement: ${CMMC_LEVEL_LABELS[contractLevel].en}.`,
    dataHandling === 'fci' ? 'Handle FCI under FAR 52.204-21. Do not generate a 800-171 SPRS number for this work.' : '',
    dataHandling === 'cui' ? 'CUI in scope: unlock SSP/POA&M, 110 practices, numeric SPRS, and a CUI enclave (PreVeil or GCC High).' : '',
    dataHandling === 'classified' ? 'Classified signal: do not bid unless an FCL path is real. CMMC Level 3 is DIBCAC, not a C3PAO.' : '',
    'CMMC Phase II C3PAO remains paused as of 13 July 2026; read DFARS 252.204-7021 on the notice.',
  ].filter(Boolean);

  return {
    companyLevel,
    contractLevel,
    dataHandling,
    assessmentPath,
    unlocks: unlocksForLevel(contractLevel, dataHandling),
    rationale: rationaleParts.join(' '),
    source: 'rules',
    classifiedAt: new Date().toISOString(),
  };
};

export const sprsNumericScore = (
  dispositions: Record<string, PracticeDisposition>,
): { score: number; met: number; notMet: number; poam: number; na: number } => {
  let deducted = 0;
  let met = 0;
  let notMet = 0;
  let poam = 0;
  let na = 0;
  CMMC_L2_PRACTICES.forEach((practice) => {
    const disposition = dispositions[practice.id] || 'not_started';
    if (disposition === 'na') {
      na += 1;
      return;
    }
    if (disposition === 'met') {
      met += 1;
      return;
    }
    if (disposition === 'poam') {
      poam += 1;
      deducted += practice.sprsWeight;
      return;
    }
    notMet += 1;
    deducted += practice.sprsWeight;
  });
  return { score: 110 - deducted, met, notMet, poam, na };
};

export const l1PassFail = (
  dispositions: Record<string, PracticeDisposition>,
): { met: number; total: number; passing: boolean } => {
  const applicable = CMMC_L1_PRACTICES.filter((practice) => (dispositions[practice.id] || 'not_started') !== 'na');
  const met = applicable.filter((practice) => (dispositions[practice.id] || 'not_started') === 'met').length;
  return { met, total: applicable.length, passing: applicable.length > 0 && met === applicable.length };
};

const asCmmcLevel = (value: unknown): CmmcLevel | null => (
  value === 'none' || value === 'l1' || value === 'l2' || value === 'l3' ? value : null
);

const asDataHandling = (value: unknown): DataHandling | null => (
  value === 'none' || value === 'fci' || value === 'cui' || value === 'classified' ? value : null
);

const asAssessmentPath = (value: unknown): DodAssessmentPath | null => (
  value === 'none' || value === 'self' || value === 'c3pao' || value === 'dibcac' ? value : null
);

export const assessmentFromGateway = (body: unknown): DodLevelAssessment | null => {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const payload = body as Record<string, unknown>;
  const contractLevel = asCmmcLevel(payload.contractLevel ?? payload.contract_level);
  const companyLevel = asCmmcLevel(payload.companyLevel ?? payload.company_level) || 'none';
  const dataHandling = asDataHandling(payload.dataHandling ?? payload.data_handling) || 'none';
  const assessmentPath = asAssessmentPath(payload.assessmentPath ?? payload.assessment_path) || 'none';
  if (!contractLevel) {
    return null;
  }
  const unlocks = Array.isArray(payload.unlocks)
    ? payload.unlocks.filter((item): item is DodCapabilityId => typeof item === 'string')
    : unlocksForLevel(contractLevel, dataHandling);
  return {
    companyLevel,
    contractLevel,
    dataHandling,
    assessmentPath,
    unlocks,
    rationale: typeof payload.rationale === 'string' ? payload.rationale : '',
    source: payload.source === 'ai' ? 'ai' : 'rules',
    classifiedAt: typeof payload.classifiedAt === 'string'
      ? payload.classifiedAt
      : typeof payload.classified_at === 'string'
        ? payload.classified_at
        : new Date().toISOString(),
  };
};

export const dispositionFromChecklistStatus = (
  status: 'not_started' | 'in_progress' | 'reviewed' | 'gap',
): PracticeDisposition => {
  if (status === 'reviewed') {
    return 'met';
  }
  if (status === 'gap') {
    return 'not_met';
  }
  if (status === 'in_progress') {
    return 'partial';
  }
  return 'not_started';
};
